import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RESPONSE_API_URL } from "config";

const baseQuery = fetchBaseQuery({
    baseUrl: RESPONSE_API_URL,
    prepareHeaders: (headers, { getState }) => {
        const user = localStorage.getItem('user');

        if (user) {
            try {
                let userToken = JSON.parse(user).token;
                headers.set('authorization', `Bearer ${userToken}`);
                headers.set('Content-Type', 'application/json');
            } catch (e) {
                console.error('Error parsing user token:', e);
            }
        }
        return headers;
    },
    credentials: "omit"
})

export const responseApi = createApi({
    reducerPath: "responseApi",
    baseQuery: baseQuery,
    tagTypes: ["TenancyReminderEmail", "UserCompletedCourses", "UserOutstandingCourses", "UserSentCourses"],
    endpoints: (builder) => ({
        sendTenancyReminderEmail: builder.mutation({
            query: ({ tenancyId }) => ({
                url: `tenancyreminder/${tenancyId}/sendemail`,
                method: 'POST'
            }),
            invalidatesTags: [{ type: "TenancyReminderEmail", id: "LIST" }]
        }),
        sendCourseReminder: builder.mutation({
            query: ({ userId, courseId, uniqueIdentifier }) => ({
                url: 'coursereminder/sendemail',
                method: 'POST',
                body: {
                    userId,
                    courseId,
                    uniqueIdentifier
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "UserOutstandingCourses", id: arg.userId },
                { type: "UserSentCourses", id: "LIST" }
            ]
        }),
        getUserCompletedCourses: builder.query({
            query: ({ userEmail }) => `universityresponse/completed?userId=${encodeURIComponent(userEmail)}`,
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status.query(),
            providesTags: (result, error, id) => [{ type: "UserCompletedCourses", id }]
        }),
        getUserOutstandingCourses: builder.query({
            query: ({ userEmail }) => `universityresponse?userId=${encodeURIComponent(userEmail)}`,
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status.query(),
            providesTags: (result, error, id) => [{ type: "UserOutstandingCourses", id }]
        }),
        deleteUserSentCourses: builder.mutation({
            query: ({ userId, uniqueIdentifier }) => ({
                url: `universityresponse/rescind/${userId}/${uniqueIdentifier}`,
                method: 'DELETE'
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response,
            invalidatesTags: (result, error, arg) => [
                { type: "UserCompletedCourses", id: arg.userId },
                { type: "UserOutstandingCourses", id: arg.userId },
                { type: "UserSentCourses", id: "LIST" },
                { type: "UserData", id: arg.userId }
            ]
        }),
        getPresignedUrlForEmailUpdate: builder.query({
            query: ({ tenancyId, fileType = 'text/csv' }) => ({
                url: `email-updates/presigned-url?tenancyId=${tenancyId}&type=${fileType}`,
                method: 'GET'
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response
        }),
        uploadEmailUpdateCsv: builder.mutation({
            queryFn: async ({ tenancyId, file }, _queryApi, _extraOptions, baseQuery) => {
                try {
                    // Step 1: Get presigned URL
                    const urlResponse = await baseQuery({
                        url: `email-updates/presigned-url?tenancyId=${tenancyId}&type=text/csv`,
                        method: 'GET'
                    });

                    if (urlResponse.error) {
                        return { error: urlResponse.error };
                    }

                    const { presignedUrl } = urlResponse.data;

                    // Step 2: Upload file to S3
                    const uploadResponse = await fetch(presignedUrl, {
                        method: 'PUT',
                        body: file,
                        headers: {
                            'Content-Type': 'text/csv',
                            'x-amz-acl': 'bucket-owner-full-control',
                            skipInterceptor: 'true'
                        }
                    });

                    if (!uploadResponse.ok) {
                        return {
                            error: {
                                status: uploadResponse.status,
                                data: 'Failed to upload file to S3'
                            }
                        };
                    }

                    return { data: { success: true } };
                } catch (error) {
                    return {
                        error: {
                            status: 'FETCH_ERROR',
                            error: error.message
                        }
                    };
                }
            }
        }),

        postXAPIStatement: builder.mutation({
            query: (statement) => ({
                url: 'universityresponse/xapi/statements',
                method: 'POST',
                body: statement
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response,
            invalidatesTags: (result, error, arg) => [
                { type: "UserCompletedCourses", id: arg.actor?.mbox?.replace('mailto:', '') },
                { type: "UserOutstandingCourses", id: arg.actor?.mbox?.replace('mailto:', '') }
            ]
        })
    }),
});

export const {
    useSendTenancyReminderEmailMutation,
    useSendCourseReminderMutation,
    useLazyGetUserCompletedCoursesQuery,
    useLazyGetUserOutstandingCoursesQuery,
    useUploadEmailUpdateCsvMutation,
    useDeleteUserSentCoursesMutation,
    usePostXAPIStatementMutation
} = responseApi;